import { useTranslate } from '@tolgee/react';
import { Tooltip } from 'antd';
import Clipboard from 'clipboard';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EmailIcon,
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import styled, { css } from 'styled-components';

import { QuestionMarkSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import useAutosaveTemplate from '../../../hooks/useAutosaveTemplate';
import { useMetrics } from '../../../hooks/useMetrics';
import { TemplateEditOrCreateDTO } from '../../../services/TemplateService/Template.type';
import { getBaseUrl } from '../../../utils/client';
import { gentlyCheckDestinationTenantUrl } from '../../../utils/navigatorUtils';
import { Input } from '../../_atoms/_Input';
import MSTeamsShareButton from '../MSTeamsShareButton';
import QRCodeShareButton from '../QRCodeShareButton';

const ShareLinkComponent = ({
  templateId,
  isGeneratedByAI,
  isCreatorSettings = false,
}: {
  templateId: string;
  isGeneratedByAI: boolean;
  isCreatorSettings?: boolean;
}) => {
  const templateRef = useRef(new Clipboard('#copy_url_template'));
  const [copyTemplateSuccess, setCopyTemplateSuccess] = useState(false);
  const { t } = useTranslate();
  const session = useSession();

  const metrics = useMetrics();
  const { autoSaveTemplate } = useAutosaveTemplate();

  useEffect(() => {
    const clip = templateRef.current;

    clip.on('success', () => {
      autoSaveTemplate({
        templateConfig: {
          isBlockedFromSharing: false,
          id: templateId,
        } as TemplateEditOrCreateDTO,
      });

      setCopyTemplateSuccess(true);
    });

    return () => {
      clip.destroy();
    };
  }, [templateId, autoSaveTemplate]);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copyTemplateSuccess) {
        metrics.logEvent('Live.Share.Template.Copy.Success', {
          templateId: templateId,
          from: 'lessonPreview',
        });
        setCopyTemplateSuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copyTemplateSuccess, metrics, templateId]);

  const shareUrl = `${getBaseUrl()}/${gentlyCheckDestinationTenantUrl()}/lessons/${templateId}/edit`;

  const shareTitle = isGeneratedByAI
    ? `${t('This lesson was generated by AI in less than 10 seconds')}`
    : '\n';

  const emailSubject = isGeneratedByAI
    ? t('I generated this lesson in 10 seconds with Curipod')
    : t('Check out my Curipod lesson');

  const handleClickShareButton = useCallback(
    (type: string) => {
      if (session.status === 'authenticated') {
        autoSaveTemplate({
          templateConfig: {
            isBlockedFromSharing: false,
            modifiedAt: new Date().getTime(),
            id: templateId,
          } as TemplateEditOrCreateDTO,
        });
      }
      metrics.logEvent('Share.Social.Media.Click', { someType: type });
    },
    [metrics, session.status, templateId, autoSaveTemplate],
  );

  return (
    <>
      {!isCreatorSettings && (
        <>
          <p style={{ fontWeight: 'bold' }}>
            {t(
              'AnyOneShareTemplate',
              'Anyone with this link can make a copy of your lesson and continue to edit. Your lesson will always remain unchanged.',
            )}
          </p>
          <Title $fontSize={18}>
            {t('Share as template')}
            <Tooltip
              title={t(
                'ShareTemplateWithCollegue',
                'Share this link with your colleague.',
              )}
            >
              <TooltipIconWrapper>
                <QuestionMarkSvg />
              </TooltipIconWrapper>
            </Tooltip>
          </Title>
        </>
      )}
      {isCreatorSettings && (
        <>
          <Title $fontSize={18}>
            {t('Share as template')}
            <Tooltip
              title={t(
                'ShareTemplateWithCollegue',
                'Share this link with your colleague.',
              )}
            >
              <TooltipIconWrapper>
                <QuestionMarkSvg />
              </TooltipIconWrapper>
            </Tooltip>
          </Title>
          <p style={{ fontWeight: 'bold' }}>
            {t(
              'AnyOneShareTemplate',
              'Anyone with this link can make a copy of your lesson and continue to edit. Your lesson will always remain unchanged.',
            )}
          </p>
        </>
      )}

      <SocialMediaContainer>
        <TwitterShareButton
          url={shareUrl}
          title={`${shareTitle}`}
          onClick={() => handleClickShareButton('twitter')}
        >
          <TwitterIcon size={32} round={true} />
        </TwitterShareButton>
        <LinkedinShareButton
          url={shareUrl}
          title={shareTitle}
          onClick={() => handleClickShareButton('linkedin')}
        >
          <LinkedinIcon size={32} round={true} />
        </LinkedinShareButton>
        <FacebookShareButton
          url={shareUrl}
          quote={shareTitle}
          onClick={() => handleClickShareButton('facebook')}
        >
          <FacebookIcon size={32} round={true} />
        </FacebookShareButton>
        <FacebookMessengerShareButton
          url={shareUrl}
          title={shareTitle}
          appId="857396192234237"
          onClick={() => handleClickShareButton('facebook')}
        >
          <FacebookMessengerIcon size={32} round={true} />
        </FacebookMessengerShareButton>
        <QRCodeShareButton url={shareUrl} />
        <a
          href={`mailto:?subject=${emailSubject}&body=${t(
            'Below is a link to my Curipod lesson.',
          )}\n\n\n ${shareUrl}`}
          onClick={() => handleClickShareButton('email')}
        >
          <EmailIcon size={32} round={true} />
        </a>
        <WhatsappShareButton
          url={shareUrl}
          title={shareTitle}
          onClick={() => handleClickShareButton('whatsapp')}
        >
          <WhatsappIcon size={32} round={true} />
        </WhatsappShareButton>
        <MSTeamsShareButton url={shareUrl} body={shareTitle} />
      </SocialMediaContainer>
      <JoinLinkContainer>
        <InputContainer
          role="button"
          id="copy_url_template"
          data-clipboard-text={shareUrl}
          $isCopied={copyTemplateSuccess}
        >
          <Input style={{ flex: '1' }} value={shareUrl} />
        </InputContainer>
      </JoinLinkContainer>
    </>
  );
};

export default ShareLinkComponent;

const SocialMediaContainer = styled.div`
  display: flex;
  padding-bottom: 0.5em;
  & > *:not(:first-child) {
    margin-left: 2px;
  }
  & > *:not(:last-child) {
    margin-right: 2px;
  }
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

const TooltipIconWrapper = styled.span`
  margin-left: 10px;
  svg {
    height: 20px;
    width: 20px;
    color: ${theme.colors.black};
  }
`;

const JoinLinkContainer = styled.div`
  display: flex;

  @media (max-width: 690px) {
    flex-direction: column;

    button {
      margin-top: 10px;
      max-width: max-content;
      align-self: center;
    }
  }
`;
const InputContainer = styled.div<{ $isCopied: boolean }>`
  display: flex;
  cursor: pointer;
  position: relative;
  width: 100%;
  margin-right: 5px;
  border-radius: 10px;

  input {
    cursor: pointer;
    ${({ $isCopied }) => $isCopied && `border: solid 1px ${theme.colors.green}`}
  }

  &:after {
    ${({ $isCopied }) =>
      $isCopied
        ? css`
            content: 'Copied';
            background-color: ${theme.colors.green} !important;
            color: white;
          `
        : css`
            content: 'Copy link';
          `}
    position: absolute;
    right: 3px;
    top: 3px;
    border-radius: 10px;
    font-weight: 700;
    background: #fff;
    padding: 7px;
  }

  @media (max-width: 690px) {
    input {
      cursor: pointer;
      font-size: 16px;
    }

    &:after {
      font-size: 16px;
      top: 3px;
    }
  }
`;
